import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import type { DataGridMAIQRef } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import { useNurturingTestAutomationProgress } from 'app/hooks/useNurturingTestAutomationProgress';
import type { APIResponseType } from 'app/apiService/types';
import HumanInTheLoopApiService from 'app/pages/HumanInTheLoopPage/apiService/humanInTheLoopApiService';
import type { AutomationNeedingAttentionResolveBody } from 'app/pages/HumanInTheLoopPage/apiService/humanInTheLoopApiService';
import type { HilOpportunityType } from 'app/pages/HumanInTheLoopPage/types';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import {
  extractAttentionRetryAllResultFromCompletionPayload,
  extractJobIdsFromResolveBody,
  extractRetryAllAcceptedJobId,
  getResolveProgressTotal,
  isNurturingAutomationHttpSuccess,
} from 'app/utils/nurturingTestAutomationProgressUtils';
import {
  findSegmentAttentionRowInQueryCache,
  resolveGridRowFromCellParams,
} from 'app/components/ReusableUIComponents/HighLevelComponents/ResolveEntitiesModalMAIQ/utils';
import {
  extractSegmentNameFromSegmentRow,
  getSegmentAttentionGridRowKey,
  getSegmentAttentionImpactedCount,
  getSegmentAttentionReasonCode,
  HIL_SEGMENTS_OPEN_RESOLVE_ACTIONS,
  HIL_SEGMENTS_RETRY,
} from 'app/utils/hilSegmentsAttentionUtils';

const HIL_SEGMENTS_ATTENTION_ENDPOINT =
  'api/v2/nurturing-automation/automation-needing-attention';

type AutomationNeedingAttentionApiResponse =
  APIResponseType<AutomationNeedingAttentionResolveBody>;

export type UseHilSegmentAttentionResolveOptions = {
  dealerId: string;
  canAccess: boolean;
  opportunityType: HilOpportunityType;
  /** Total functional segments needing attention (all pages). */
  functionalSegmentsCount: number;
  onRefreshGrid?: () => void;
  dataGridRef?: RefObject<DataGridMAIQRef>;
};

export function useHilSegmentAttentionResolve(
  options: UseHilSegmentAttentionResolveOptions,
) {
  const { dealerId, canAccess, opportunityType, functionalSegmentsCount, onRefreshGrid, dataGridRef: externalDataGridRef } =
    options;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const internalDataGridRef = useRef<DataGridMAIQRef>(null);
  const dataGridRef: RefObject<DataGridMAIQRef> =
    externalDataGridRef ?? internalDataGridRef;
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [resolveModalRow, setResolveModalRow] = useState<unknown>(null);
  const [resolveApiResponseBody, setResolveApiResponseBody] =
    useState<AutomationNeedingAttentionResolveBody | null>(null);
  const [resolveResponseVersion, setResolveResponseVersion] = useState(0);
  const [showResolveProgress, setShowResolveProgress] = useState(false);
  const [resolveActionLoading, setResolveActionLoading] = useState(false);
  const [retryAllConfirmOpen, setRetryAllConfirmOpen] = useState(false);
  const [retryAllLoading, setRetryAllLoading] = useState(false);
  const [retryAllInProgress, setRetryAllInProgress] = useState(false);
  const retryAllSessionRef = useRef(false);

  const invalidateSegmentsAttentionCounts = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ['hil-segments-attention-count', dealerId, opportunityType],
    });
  }, [dealerId, opportunityType, queryClient]);

  const automation = useNurturingTestAutomationProgress({
    dealerId,
    canAccess,
    persistenceVariant: 'hil-retry-all',
    onSessionRestored: () => {
      retryAllSessionRef.current = true;
      setRetryAllInProgress(true);
      setShowResolveProgress(true);
    },
    progressTitle: retryAllInProgress
      ? 'Retrying all segments...'
      : 'Resolving segment issues...',
    progressCountLabel: retryAllInProgress ? 'segments' : 'leads',
    invalidatePrefixesOnComplete: [
      {
        prefix: HIL_SEGMENTS_ATTENTION_ENDPOINT,
        method: 'GET',
      },
    ],
    onComplete: () => {
      invalidateSegmentsAttentionCounts();
      onRefreshGrid?.();
    },
  });

  const automationRef = useRef(automation);
  automationRef.current = automation;

  useEffect(() => {
    if (!retryAllSessionRef.current || !automation.automationCompletionPayload) {
      return;
    }
    const batchBody = extractAttentionRetryAllResultFromCompletionPayload(
      automation.automationCompletionPayload,
    );
    if (!batchBody) return;

    setResolveApiResponseBody(batchBody);
    setResolveResponseVersion(prev => prev + 1);
    retryAllSessionRef.current = false;
    setRetryAllInProgress(false);
    setShowResolveProgress(false);
  }, [automation.automationCompletionPayload]);

  const setRowRetrying = useCallback((row: unknown, retrying: boolean) => {
    const rowKey = getSegmentAttentionGridRowKey(row);
    if (!rowKey) return;
    dataGridRef.current?.manualUpdateData(rowKey, { __retrying: retrying });
  }, []);

  const openResolveModal = useCallback(
    (cellParams: unknown) => {
      const clickedRow = resolveGridRowFromCellParams(cellParams);
      const freshestRow = findSegmentAttentionRowInQueryCache(
        queryClient,
        clickedRow,
      );
      setResolveModalRow(freshestRow);
      setResolveModalOpen(true);
    },
    [queryClient],
  );

  const closeResolveModal = useCallback(() => {
    setResolveModalOpen(false);
    setResolveModalRow(null);
  }, []);

  const handleResolveResponse = useCallback(
    async (
      response: AutomationNeedingAttentionApiResponse,
      options?: { fallbackImpactedTotal?: number; closeResolveModal?: boolean },
    ) => {
      if (!isNurturingAutomationHttpSuccess(response.statusCode)) {
        const msg = response.message ? String(response.message) : '';
        if (msg) {
          dispatch(
            notificationAlertActions.addNotification({
              open: true,
              severity: 'error',
              message: msg,
            }),
          );
        }
        return;
      }

      const body = response.body ?? null;
      setResolveApiResponseBody(body);
      setResolveResponseVersion(prev => prev + 1);

      const isTest = opportunityType === 'test';
      const reruns = Array.isArray(body?.reruns) ? body.reruns : [];
      const hasReruns = reruns.length > 0;
      const jobIds = extractJobIdsFromResolveBody(body);

      if (isTest && hasReruns && jobIds.length > 0) {
        const progressTotal = getResolveProgressTotal(
          body,
          options?.fallbackImpactedTotal ?? 1,
        );
        automationRef.current.startSession({
          jobIds,
          progressTotal,
          primaryJobId: jobIds[0],
        });
        setShowResolveProgress(true);
      } else {
        automationRef.current.resetSession();
        setShowResolveProgress(false);
        invalidateSegmentsAttentionCounts();
        onRefreshGrid?.();
      }

      const successMessage = response.message ? String(response.message) : '';
      if (successMessage) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message: successMessage,
          }),
        );
      }

      if (options?.closeResolveModal) {
        setResolveModalOpen(false);
        setResolveModalRow(null);
      }
    },
    [
      dispatch,
      invalidateSegmentsAttentionCounts,
      onRefreshGrid,
      opportunityType,
    ],
  );

  const resolveRow = useCallback(
    async (row: unknown) => {
      const segmentName = extractSegmentNameFromSegmentRow(row);
      const reasonCode = getSegmentAttentionReasonCode(row);
      if (!dealerId.trim() || !segmentName || !reasonCode) return;

      setResolveActionLoading(true);
      setRowRetrying(row, true);
      try {
        const response =
          await HumanInTheLoopApiService.postAutomationNeedingAttentionResolve({
            dealer_id: dealerId,
            segment_name: segmentName,
            reason_code: reasonCode,
            opportunity_type: opportunityType,
          });

        await handleResolveResponse(response, {
          fallbackImpactedTotal: getSegmentAttentionImpactedCount(row),
          closeResolveModal: true,
        });
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : '';
        if (msg) {
          dispatch(
            notificationAlertActions.addNotification({
              open: true,
              severity: 'error',
              message: msg,
            }),
          );
        }
      } finally {
        setRowRetrying(row, false);
        setResolveActionLoading(false);
      }
    },
    [
      dealerId,
      dispatch,
      handleResolveResponse,
      opportunityType,
      setRowRetrying,
    ],
  );

  const openRetryAllConfirm = useCallback(() => {
    if (functionalSegmentsCount <= 0) {
      dispatch(
        notificationAlertActions.addNotification({
          open: true,
          severity: 'info',
          message: 'No functional segments needing attention to retry.',
        }),
      );
      return;
    }
    setRetryAllConfirmOpen(true);
  }, [dispatch, functionalSegmentsCount]);

  const closeRetryAllConfirm = useCallback(() => {
    if (retryAllLoading) return;
    setRetryAllConfirmOpen(false);
  }, [retryAllLoading]);

  const confirmRetryAll = useCallback(async () => {
    if (!dealerId.trim() || functionalSegmentsCount <= 0) return;

    automationRef.current.prepareSession({ progressTotal: functionalSegmentsCount });
    setRetryAllInProgress(true);
    setShowResolveProgress(true);
    setRetryAllLoading(true);
    try {
      const response =
        await HumanInTheLoopApiService.postAutomationNeedingAttentionRetryAll({
          dealer_id: dealerId,
          opportunity_type: opportunityType,
          error_category: 'functional',
          total_segments_hint: functionalSegmentsCount,
        });

      if (response.statusCode === 202) {
        const jobId = extractRetryAllAcceptedJobId(response.body);
        if (jobId) {
          retryAllSessionRef.current = true;
          automationRef.current.startSession({
            jobIds: [jobId],
            progressTotal: functionalSegmentsCount,
            primaryJobId: jobId,
          });
        }

        const acceptedMessage = response.message ? String(response.message) : '';
        if (acceptedMessage) {
          dispatch(
            notificationAlertActions.addNotification({
              open: true,
              severity: 'success',
              message: acceptedMessage,
            }),
          );
        }
        setRetryAllConfirmOpen(false);
        return;
      }

      const fallbackImpactedTotal =
        typeof response.body?.rescheduled_count === 'number' &&
        response.body.rescheduled_count > 0
          ? response.body.rescheduled_count
          : functionalSegmentsCount;

      await handleResolveResponse(response, { fallbackImpactedTotal });
      setRetryAllConfirmOpen(false);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : '';
      if (msg) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'error',
            message: msg,
          }),
        );
      }
    } finally {
      setRetryAllLoading(false);
    }
  }, [
    dealerId,
    dispatch,
    functionalSegmentsCount,
    handleResolveResponse,
    opportunityType,
  ]);

  const resolveRowRef = useRef(resolveRow);
  resolveRowRef.current = resolveRow;

  const handleCellClick = useCallback(
    (params: { row?: unknown }, clickedElement?: string) => {
      if (clickedElement === HIL_SEGMENTS_RETRY) {
        void resolveRowRef.current(params?.row);
        return;
      }

      if (clickedElement === HIL_SEGMENTS_OPEN_RESOLVE_ACTIONS) {
        openResolveModal(params);
      }
    },
    [openResolveModal],
  );

  const handleResolveModalSuccess = useCallback(() => {
    onRefreshGrid?.();
  }, [onRefreshGrid]);

  return {
    dataGridRef,
    handleCellClick,
    resolveRow,
    resolveModalOpen,
    resolveModalRow,
    closeResolveModal,
    handleResolveModalSuccess,
    resolveApiResponseBody,
    resolveResponseVersion,
    showResolveProgress,
    resolveActionLoading,
    retryAllConfirmOpen,
    retryAllLoading,
    retryAllInProgress,
    functionalSegmentsCount,
    openRetryAllConfirm,
    closeRetryAllConfirm,
    confirmRetryAll,
    ...automation,
  };
}
